import store from "@/store";
import { InspectKbn, SeibiKbn } from "@/model/InspectKbn";
import { InspectStaff } from "@/model/InspectStaff";
import { LiftUse } from "@/model/LiftUse";
import { InspectDetail } from "@/model/InspectDetail";
import { InspectionDetailState } from "@/store/modules/inspectionDetailStore";
import { IppanSeibiService } from "@/service/IppanSeibiService";

const namespace = "InspectionDetail";

export class InspectionLiftUseDetailStoreService {
  private ippanSeibiService: IppanSeibiService;
  deSelLiftUse: boolean = false;
  ownLift: string = "";

  // サービス初期化時に使用リフトの選択管理枠をStoreへ登録する
  constructor() {
    this.ippanSeibiService = new IppanSeibiService();
    this.addNowSelected();
  }

  // InspectionDetail Store全体の現在状態を参照する
  get currentState(): InspectionDetailState {
    return store.getters[`${namespace}/currentState`];
  }

  // 使用リフトが選択済みかどうかを参照する
  get nowSelected(): boolean {
    const nowSelected = store.getters[`${namespace}/getNowSelected`];
    if (nowSelected.liftUse === true || nowSelected.liftUse === false) {
      return nowSelected.liftUse;
    }
    this.addNowSelected();
    return false;
  }

  // 使用リフト一覧の編集用データを参照する
  get liftUse(): LiftUse[] {
    return this.currentState.liftUse;
  }

  // 検査構成画面の入力を減らす設定かどうかを参照する
  get isInspectionLessInput(): boolean | null {
    return store.getters["FunctionSetting/isInspectionLessInput"];
  }

  // 使用リフトに「選択無し」を出す条件かどうかを返す
  get hasDeSelLift(): boolean | null {
    return this.ippanSeibiService.isIppanSeibi || this.isInspectionLessInput;
  }

  // 使用リフト一覧をStoreへ反映する
  setLiftUse(payload: LiftUse[]) {
    return store.dispatch(`${namespace}/setLiftUse`, payload);
  }

  // 使用リフトの選択管理枠をStoreへ追加する
  addNowSelected() {
    return store.dispatch(`${namespace}/addNowSelected`, "liftUse");
  }

  // 使用リフトの選択済み状態をStoreへ反映する
  setNowSelected(payload: boolean) {
    return store.dispatch(`${namespace}/setNowSelected`, {
      key: "liftUse",
      value: payload
    });
  }

  // 使用リフトの選択状態からスタート可否判定用の状態を更新する
  checkDisableStart(arrData: any, isUnSelect?: boolean) {
    let checked: boolean = false;
    if (isUnSelect) {
      checked = true;
    } else if (arrData) {
      checked = arrData.some((data: any) => data.selected == "1");
    }
    this.setNowSelected(checked);
  }

  // 共通処理から扱う使用リフト一覧を返す
  get list(): LiftUse[] {
    return this.liftUse;
  }

  // 共通処理から使用リフト一覧をStoreへ反映する
  setList(payload: LiftUse[]) {
    return this.setLiftUse(payload);
  }

  // テンプレート表示用の使用リフト選択肢リストを生成する
  getTargetList() {
    return this.list.map((data: any) => {
      return {
        "mode": 2,
        "btnDel": false,
        "btnSel": data.selected == '1',
        "btnFre": data.selected == '0' && (data.sknListUseKbnCd == 0 || this.ownLift),
        "btnUse": data.sknListUseKbnCd == 1 && data.selected == 0 && data.num != this.ownLift,
        "key": data.num,
        "dataSet": [data.num, data.sknListUseKbnCd,data.srPlateNo],
        "isDisabled": false,
        "icon": "icon-service",
        "label": data.num,
        "labelEntends1": data.sknListUseKbnNm,
        "labelEntends2": data.srPlateNo,
        "labelEntends3": data.srModelNm,
      };
    });
  }

  // スタート時に選択中の使用リフトを保存データへ反映する
  setStartInspectDetail(inspectionDetail: InspectDetail) {
    inspectionDetail.liftUse = '0';
    const selectedData = this.list.find((data: LiftUse) => {
      return data.selected == "1";
    });
    if (!selectedData) { return; }

    inspectionDetail.liftUse = selectedData.num;
  }

  // 取得済みの検査構成から使用リフトの初期選択情報を反映する
  applyInitialInspectionDetail(data: any, inspectionDetail?: InspectDetail) {
    this.ownLift = data.liftUse;
  }

  // 使用リフトの選択クリックを処理する
  select(selectData: any) {
    const num = selectData[0];
    const isUse = selectData[1];
    const plateNo = selectData[2];
    const liftUse = this.list;
    let message = "";
    liftUse.map(data => {
      if (data.num == num) {
        if (data.selected == "1") {
          data.selected = "0";
        } else {
          if (isUse == "1") {
            message = `使用中のリフト${num}(${plateNo})を選択しました。`;
          }
          this.deSelLiftUse = false;
          data.selected = "1";
        }
      } else {
        data.selected = "0";
      }
    });

    this.setList([...liftUse]);
    this.checkDisableStart(liftUse, this.deSelLiftUse);
    return { deSelLiftUse: this.deSelLiftUse, message };
  }

  // 使用リフトの「選択無し」状態を切り替える
  deSelect(hasDeSelLift: boolean) {
    if (!hasDeSelLift) {
      return this.deSelLiftUse;
    }
    const liftUse = this.list;
    if (!this.deSelLiftUse) {
      liftUse.map(data => (data.selected = "0"));
    }
    this.deSelLiftUse = !this.deSelLiftUse;
    this.setList([...liftUse]);
    this.checkDisableStart(liftUse, this.deSelLiftUse);
    return this.deSelLiftUse;
  }

  // 使用リフトで「選択無し」を表示できるか返す
  canDeSelect(hasDeSelLift: boolean) {
    return hasDeSelLift;
  }

  // 使用リフトが「選択無し」状態か返す
  isDeSelected() {
    return this.deSelLiftUse;
  }

  // 共通呼び出し用の「選択無し」クリック処理
  clickDeSelect(hasDeSelLift: boolean) {
    return this.deSelect(hasDeSelLift);
  }

  // 使用リフト一覧をリフト番号順に並べる
  sortList(liftUse: LiftUse[]) {
    return liftUse.sort((a: any, b: any) => {
      a = a.num;
      b = b.num;
      return a === b ? 0 : a > b ? 1 : -1;
    });
  }

  // Storeに既存リストがある場合に選択状態を再評価する
  applyExistingList(inspectionLiftUse: string | undefined, hasDeSelLift: boolean) {
    const liftUse = this.sortList(this.list);
    this.setList(liftUse);
    if (hasDeSelLift) {
      // 使用リフトは特定の条件の時だけ選択無しが出てくるので
      // 特定条件時だけ選択無しを選んでいるか調べて進行チェックさせる
      if (!inspectionLiftUse || inspectionLiftUse === "0") {
        const filterData = liftUse.filter((e: LiftUse) => e.selected === "1");
        if (filterData && filterData.length == 0) {
          this.deSelLiftUse = true;
        }
      }
      this.checkDisableStart(liftUse, this.deSelLiftUse);
    } else {
      this.checkDisableStart(liftUse);
    }
    return {
      liftUse,
      deSelLiftUse: this.deSelLiftUse
    };
  }

  // API取得結果から使用リフト一覧の初期選択状態を作成する
  setInitialList(res: LiftUse[], inspectionLiftUse: string | undefined) {
    const liftUse = res.map(data => {
      if (inspectionLiftUse && inspectionLiftUse == data.num) {
        data.selected = "1";
        this.setNowSelected(true);
      } else {
        data.selected = "0";
      }
      return data;
    });
    this.sortList(liftUse);
    this.deSelLiftUse = false;
    this.setList(liftUse);
    if (this.hasDeSelLift && (!inspectionLiftUse || inspectionLiftUse === "0") && !this.nowSelected) {
      // 初期値「選択無し」を選択
      this.setNowSelected(true);
      this.deSelLiftUse = true;
    }
    return {
      liftUse,
      deSelLiftUse: this.deSelLiftUse
    };
  }

  // 使用リフト一覧を既存StoreまたはAPIから取得する
  getDataList(
    loadInitialList: () => Promise<LiftUse[]>,
    inspectionLiftUse: string | undefined
  ) {
    if (this.list.length > 0) {
      this.applyExistingList(inspectionLiftUse, !!this.hasDeSelLift);
      return {
        hasExistingList: true,
        promise: null
      };
    }
    return {
      hasExistingList: false,
      promise: loadInitialList().then((res: LiftUse[]) => {
        return this.setInitialList(res, inspectionLiftUse);
      })
    };
  }
}
